import React, { useEffect, useRef, useState } from 'react';
import '../assets/css/systemManagement.css';
import { RidersModel } from '../models/RidersModel';
import {
    FilterDataModel,
    SortModel,
    SortDirection,
    RiderPackageModel,
    RiderServiceBranch,
    ActivityStatus,
} from '../models/superbit-api-models';
import DeleteAlert from '../components/DeleteAlert';
import NewRiderPackage from './_NewRiderPackage';

// React port of Views/SystemManagement/Riders/_RiderPackages.cshtml (+ the
// _RiderPackagesItems.cshtml grid rows and the sortRiderPackages / include-in-
// policy-price / delete handlers from wwwroot/js/systemManagement.js).
// The server (RiderBL.SortPackage) sorts by the requested column and applies the
// optional search term but does NOT paginate, so the whole list renders at once.

// Sortable columns. `sortBy` matches the property names the old MVC view sent via
// data-sort-by so the existing server-side sorting keeps working. Columns without
// a sortBy (the checkbox toggle and the free-text services list) are not sortable.
type Column = { key: string; label: string; sortBy?: string };
const COLUMNS: Column[] = [
    { key: 'name', label: 'שם חבילה', sortBy: 'Name' },
    { key: 'include', label: 'כלול במחיר הפוליסה' },
    { key: 'branch', label: 'ענף', sortBy: 'RiderServiceBranch' },
    { key: 'status', label: 'סטטוס', sortBy: 'Status' },
    { key: 'agentCost', label: 'עלות לסוכן', sortBy: 'AgentCost' },
    { key: 'recommendedCost', label: 'עלות מומלצת ללקוח', sortBy: 'RecommendedCost' },
    { key: 'services', label: 'שירותים' },
];

const branchName = (b?: RiderServiceBranch) => (b === RiderServiceBranch._1 ? 'דירה' : 'רכב');
const statusName = (s?: ActivityStatus) => (s === ActivityStatus._0 ? 'פעיל' : 'לא פעיל');

const RidersManagement = () => {
    const [ridersModel] = useState<RidersModel>(() => new RidersModel());

    const [packages, setPackages] = useState<RiderPackageModel[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const [searchTerm, setSearchTerm] = useState<string>('');
    const [sortProp, setSortProp] = useState<string | undefined>(undefined);
    const [sortDir, setSortDir] = useState<SortDirection>(SortDirection._0);
    const [reloadTick, setReloadTick] = useState<number>(0); // bumped to force a reload (e.g. on refresh/clear)

    const [openMenuId, setOpenMenuId] = useState<string | undefined>(undefined);
    const [deleteId, setDeleteId] = useState<string | undefined>(undefined);

    // Add/edit modal. editId set = edit; undefined while the modal is open = add.
    const [showModal, setShowModal] = useState<boolean>(false);
    const [editId, setEditId] = useState<string | undefined>(undefined);

    const buildFilter = (): FilterDataModel => {
        const filter = new FilterDataModel();
        filter.searchTerm = searchTerm ? searchTerm : undefined;
        if (sortProp) {
            const sort = new SortModel();
            sort.property = sortProp;
            sort.sortDirection = sortDir;
            filter.sortBy = [sort];
        }
        return filter;
    };

    const load = async () => {
        setLoading(true);
        try {
            const list = await ridersModel.sortPackages(buildFilter());
            setPackages(list);
        } catch (e) {
            console.error('Failed to load rider packages', e);
            setPackages([]);
        } finally {
            setLoading(false);
        }
    };

    // Reload whenever sort changes (reloadTick forces a reload).
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortProp, sortDir, reloadTick]);

    const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') load();
    };

    // Refresh button: clear the search filter and reload (reloadTick guarantees the reload).
    const clearFilters = () => {
        setSearchTerm('');
        setReloadTick((t) => t + 1);
    };

    // Auto-search: reload shortly after the user stops typing (skip the initial mount).
    const searchDidMount = useRef(false);
    useEffect(() => {
        if (!searchDidMount.current) {
            searchDidMount.current = true;
            return;
        }
        const t = setTimeout(() => load(), 400);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    const onSort = (column: Column) => {
        if (!column.sortBy) return;
        if (sortProp === column.sortBy) {
            setSortDir(sortDir === SortDirection._0 ? SortDirection._1 : SortDirection._0);
        } else {
            setSortProp(column.sortBy);
            setSortDir(SortDirection._0);
        }
    };

    const toggleMenu = (id?: string) => {
        if (!id) return;
        setOpenMenuId(openMenuId === id ? undefined : id);
    };

    // Inline "include in policy price" toggle — posts just the flag for this id
    // (Rider/UpdateIncludeInPolicyPrice) and updates the row optimistically.
    const onToggleIncludeInPolicyPrice = async (pkg: RiderPackageModel, checked: boolean) => {
        if (!pkg.id) return;
        setPackages((prev) =>
            prev.map((p) => (p.id === pkg.id ? Object.assign(p, { includeInPolicyPrice: checked }) : p))
        );
        try {
            const body = new RiderPackageModel();
            body.id = pkg.id;
            body.includeInPolicyPrice = checked;
            await ridersModel.updateIncludeInPolicyPrice(body);
        } catch (e) {
            console.error('Failed to update includeInPolicyPrice', e);
            // Revert on failure.
            setPackages((prev) =>
                prev.map((p) =>
                    p.id === pkg.id ? Object.assign(p, { includeInPolicyPrice: !checked }) : p
                )
            );
        }
    };

    const onAdd = () => {
        setEditId(undefined);
        setShowModal(true);
    };

    const onEdit = (pkg: RiderPackageModel) => {
        setOpenMenuId(undefined);
        setEditId(pkg.id);
        setShowModal(true);
    };

    const confirmDelete = async () => {
        if (!deleteId) return;
        try {
            await ridersModel.deletePackage(deleteId);
            await load();
        } catch (e) {
            console.error('Failed to delete rider package', e);
        } finally {
            setDeleteId(undefined);
        }
    };

    return (
        <div className="users-management rtl">
            <div className="users-toolbar flex-centered">
                <div className="users-search">
                    <span className="search-icon" />
                    <input
                        type="text"
                        className="users-search__input"
                        placeholder="חיפוש"
                        autoComplete="off"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={onSearchKeyDown}
                    />
                </div>
                <div className="users-refresh pointer" title="רענון" onClick={() => clearFilters()} />
            </div>

            <div className="users-table-wrapper">
                <table className="users-table">
                    <thead>
                        <tr>
                            {COLUMNS.map((col) => (
                                <th
                                    key={col.key}
                                    className={`users-th ${col.sortBy ? 'sortable pointer' : ''} ${
                                        sortProp === col.sortBy ? 'sorted' : ''
                                    }`}
                                    onClick={() => onSort(col)}
                                >
                                    <span>{col.label}</span>
                                    {col.sortBy && (
                                        <span
                                            className={`arrow-updown ${
                                                sortProp === col.sortBy
                                                    ? sortDir === SortDirection._0
                                                        ? 'asc'
                                                        : 'desc'
                                                    : ''
                                            }`}
                                        />
                                    )}
                                </th>
                            ))}
                            <th className="users-th users-th--add">
                                <div className="add-btn pointer" onClick={onAdd}>
                                    <span className="add-file" />
                                    <span className="text">הוסף</span>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {packages.map((pkg) => (
                            <tr key={pkg.id}>
                                <td className="bold">{pkg.name}</td>
                                <td>
                                    <input
                                        type="checkbox"
                                        checked={!!pkg.includeInPolicyPrice}
                                        onChange={(e) =>
                                            onToggleIncludeInPolicyPrice(pkg, e.target.checked)
                                        }
                                    />
                                </td>
                                <td>{branchName(pkg.riderServiceBranch)}</td>
                                <td>{statusName(pkg.status)}</td>
                                <td>{pkg.agentCost} ₪</td>
                                <td>{pkg.recommendedCost} ₪</td>
                                <td>{pkg.riderPackageServicesString}</td>
                                <td className="users-actions">
                                    <button
                                        type="button"
                                        className="actionBtn pointer"
                                        onClick={() => toggleMenu(pkg.id)}
                                    >
                                        <div className="edit-icon" />
                                        <span>פעולות</span>
                                        <div className="dropdown-icon" />
                                    </button>
                                    {openMenuId === pkg.id && (
                                        <ul className="actionBtnMenu">
                                            <li
                                                className="actionBtnMenuItem"
                                                onClick={() => onEdit(pkg)}
                                            >
                                                ערוך
                                            </li>
                                            <li
                                                className="actionBtnMenuItem"
                                                onClick={() => {
                                                    setOpenMenuId(undefined);
                                                    setDeleteId(pkg.id);
                                                }}
                                            >
                                                מחק
                                            </li>
                                        </ul>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {!loading && packages.length === 0 && (
                            <tr>
                                <td className="users-empty" colSpan={COLUMNS.length + 1}>
                                    לא נמצאו חבילות
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <DeleteAlert
                show={!!deleteId}
                onClose={() => setDeleteId(undefined)}
                onConfirm={confirmDelete}
            />

            {showModal && (
                <NewRiderPackage
                    show={showModal}
                    packageId={editId}
                    onClose={() => setShowModal(false)}
                    onSaved={() => {
                        setShowModal(false);
                        load();
                    }}
                />
            )}
        </div>
    );
};

export default RidersManagement;
export {};
